import { useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { ProfessionalMeteringInfo } from '@common/credits/ProfessionalEdition/types';
import { useProfessionalEditionCreditsContext } from '@common/credits/ProfessionalEdition/useProfessionalEditionCreditsContext';
import { convertCreditsToUSD } from '@common/credits/ProfessionalEdition/utils';
import { postMessageToOpener } from '@common/oAuth/utils';
import { CurrencyDollarIcon, SlashCircleAIcon, TrashDIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Dialog } from '@prophecy/ui-v3/Dialog';
import { DialogSize } from '@prophecy/ui-v3/Dialog/types';
import { NumberInput } from '@prophecy/ui-v3/Input';
import { InputSize } from '@prophecy/ui-v3/Input/types';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Separator } from '@prophecy/ui-v3/Separator';
import { toast } from '@prophecy/ui-v3/Toast';
import { Text } from '@prophecy/ui-v3/Typography';
import { FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { useDependantState } from '@prophecy/utils/react/hooks';
import { useState } from 'react';
import styled from 'styled-components';

import { updateUsageLimit } from '../data/api';

const defaultUsageLimit = FROZEN_EMPTY_OBJECT as Required<ProfessionalMeteringInfo>['usageLimit'];
export const ManageMonthlySpendingDialog = ({ onClose }: { onClose: () => void }) => {
  const {
    updateMeteringInfo,
    meteringInfo: { activatedUsers }
  } = useProfessionalEditionCreditsContext();
  const {
    usageLimit = defaultUsageLimit,
    teamID,
    serviceCreditsOverage,
    pricePerCredit
  } = useProfessionalCreditStatus();

  const [monthlyUsageLimit, setMonthlyUsageLimit] = useDependantState(
    (usageLimit) => ({
      alertAmount: usageLimit.alertAmount,
      budgetAmount: usageLimit.budgetAmount,
      seats: usageLimit.seats
    }),
    [usageLimit]
  );

  const [error, setError] = useState<string | null>(null);

  const validateUsageLimit = () => {
    const currentSpending = convertCreditsToUSD(serviceCreditsOverage, pricePerCredit);

    if (monthlyUsageLimit.seats !== undefined) {
      if (monthlyUsageLimit.seats < 0) {
        setError('Seat limit cannot be negative');
        return false;
      }
      if (monthlyUsageLimit.seats === 0) {
        setError('Seat limit cannot be zero');
        return false;
      }
      if (monthlyUsageLimit.seats < activatedUsers) {
        setError(`Seat limit cannot be less than your current number of users (${activatedUsers})`);
        return false;
      }
    }

    if (monthlyUsageLimit.alertAmount !== undefined) {
      if (monthlyUsageLimit.alertAmount < 0) {
        setError('Usage alert limit cannot be negative');
        return false;
      }
      if (monthlyUsageLimit.alertAmount === 0) {
        setError('Usage alert limit cannot be zero');
        return false;
      }

      if (monthlyUsageLimit.alertAmount <= currentSpending) {
        setError(`Usage alert limit must be greater than your current additional usage amount ($${currentSpending})`);
        return false;
      }
    }

    if (monthlyUsageLimit.budgetAmount !== undefined) {
      if (monthlyUsageLimit.budgetAmount < 0) {
        setError('Usage budget limit cannot be negative');
        return false;
      }
      if (monthlyUsageLimit.budgetAmount === 0) {
        setError('Usage budget limit cannot be zero');
        return false;
      }

      if (monthlyUsageLimit.budgetAmount <= currentSpending) {
        setError(`Usage budget limit must be greater than your current additional usage amount ($${currentSpending})`);
        return false;
      }
    }

    if (monthlyUsageLimit.budgetAmount && monthlyUsageLimit.alertAmount) {
      if (monthlyUsageLimit.budgetAmount < monthlyUsageLimit.alertAmount) {
        setError('Usage budget limit cannot be less than usage alert limit');
        return false;
      }
      if (monthlyUsageLimit.budgetAmount === monthlyUsageLimit.alertAmount) {
        setError('Usage budget limit cannot be same as usage alert limit');
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleSave = async () => {
    try {
      if (!validateUsageLimit()) {
        return;
      }
      const { success } = await updateUsageLimit(teamID, monthlyUsageLimit);
      if (success) {
        toast.success({
          content: 'Usage limit updated successfully',
          'data-test-id': 'usage-limit-updated-successfully'
        });
        updateMeteringInfo({ usageLimit: monthlyUsageLimit });
        postMessageToOpener('update-limit-success', undefined);
      } else {
        toast.error({ content: 'Failed to update usage limit', 'data-test-id': 'failed-to-update-usage-limit' });
      }
      onClose();
    } catch (error) {
      console.error(error);
      toast.error({ content: 'Failed to update usage limit', 'data-test-id': 'failed-to-update-usage-limit' });
    }
  };

  return (
    <StyledDialog
      open={true}
      data-test-id={'manage-monthly-spending-dialog'}
      title='Manage monthly spending'
      okButton={{ children: 'Save' }}
      onOk={handleSave}
      closeButton={{ children: 'Cancel' }}
      size={DialogSize.l}
      onClose={onClose}
      showCloseIcon={true}>
      <ManageMonthlySpendWrapper direction='vertical' gap={theme.spaces.x12}>
        <Text level='md' tone={theme.colors.gray900}>
          Set a user seat limit
        </Text>
        <Text level='sm' tone={theme.colors.gray600}>
          Choose a maximum number of users that can be added to your team.
        </Text>
      </ManageMonthlySpendWrapper>
      <StyledInputContainer direction='horizontal' gap={theme.spaces.x24}>
        <NumberInput
          data-test-id={'seat-limit-input'}
          placeholder='Enter seat limit'
          inputSize={InputSize.m}
          value={monthlyUsageLimit.seats}
          onChange={(value) => setMonthlyUsageLimit({ ...monthlyUsageLimit, seats: value })}
          aria-label='Enter seat limit'
          allowNegative={false}
        />
        {monthlyUsageLimit.seats !== undefined && (
          <Button
            variant='plain'
            size='m'
            aria-label='Remove user seat limit'
            data-test-id={'set-seat-limit-button'}
            onClick={() => setMonthlyUsageLimit({ ...monthlyUsageLimit, seats: undefined })}
            icon={<TrashDIcon type={'default'} color={theme.colors.gray500} />}
          />
        )}
      </StyledInputContainer>

      <ManageMonthlySpendWrapper direction='vertical' gap={theme.spaces.x12}>
        <Text level='md' tone={theme.colors.gray900}>
          Set a usage alert
        </Text>
        <Text level='sm' tone={theme.colors.gray600}>
          Choose an amount, and we’ll notify you if your spending reaches it - no interruptions, just a helpful
          heads-up.
        </Text>
      </ManageMonthlySpendWrapper>
      <StyledInputContainer direction='horizontal' gap={theme.spaces.x24}>
        <NumberInput
          data-test-id={'usage-alert-input'}
          icon={<CurrencyDollarIcon type={'default'} color={theme.colors.gray500} />}
          iconPlacement='left'
          placeholder='Enter amount'
          inputSize={InputSize.m}
          value={monthlyUsageLimit.alertAmount}
          onChange={(value) => setMonthlyUsageLimit({ ...monthlyUsageLimit, alertAmount: value })}
          aria-label='Enter usage alert amount'
          allowNegative={false}
        />
        {monthlyUsageLimit.alertAmount !== undefined && (
          <Button
            variant='plain'
            size='m'
            data-test-id={'set-usage-alert-button'}
            onClick={() => setMonthlyUsageLimit({ ...monthlyUsageLimit, alertAmount: undefined })}
            icon={<TrashDIcon type={'default'} color={theme.colors.gray500} />}
            aria-label='Remove usage alert'
          />
        )}
      </StyledInputContainer>
      <StyledSeparator />
      <ManageMonthlySpendWrapper direction='vertical' gap={theme.spaces.x12}>
        <Stack direction='horizontal' align='space-between'>
          <Text level='md' tone={theme.colors.gray900}>
            Set a usage budget
          </Text>
          <Stack direction='horizontal' gap={theme.spaces.x8}>
            <SlashCircleAIcon type={'solid'} color={theme.colors.error600} />
            <Text level='sm' tone={theme.colors.error600} weight={theme.fontWeight.semiBold}>
              Triggers service interruption
            </Text>
          </Stack>
        </Stack>
        <Text level='sm' tone={theme.colors.gray600}>
          When this limit is reached, services will be suspended until the budget is increased or the next billing cycle
          begins. This will help prevent extra charges.
        </Text>
        <Text level='sm' tone={theme.colors.gray600}>
          <StyledUnorderedList>
            <li>Cloud services, deployments and agent access will all be paused</li>
            <li>Any active deployments will go offline, so users won’t have access</li>
            <li>Deployments must be manually restarted after suspension.</li>
          </StyledUnorderedList>
        </Text>
      </ManageMonthlySpendWrapper>
      <StyledInputContainer direction='horizontal' gap={theme.spaces.x24}>
        <NumberInput
          data-test-id={'usage-budget-input'}
          icon={<CurrencyDollarIcon type={'default'} color={theme.colors.gray500} />}
          iconPlacement='left'
          placeholder='Enter amount'
          inputSize={InputSize.m}
          value={monthlyUsageLimit.budgetAmount}
          onChange={(value) => setMonthlyUsageLimit({ ...monthlyUsageLimit, budgetAmount: value })}
          aria-label='Usage budget amount'
          allowNegative={false}
        />
        {monthlyUsageLimit.budgetAmount !== undefined && (
          <Button
            variant='plain'
            size='m'
            data-test-id={'set-usage-budget-button'}
            onClick={() => setMonthlyUsageLimit({ ...monthlyUsageLimit, budgetAmount: undefined })}
            icon={<TrashDIcon type={'default'} color={theme.colors.gray500} />}
            aria-label='Remove usage budget'
          />
        )}
      </StyledInputContainer>

      <StyledErrorContainer>
        <Text level='sm' tone={theme.colors.error600}>
          {error}
        </Text>
      </StyledErrorContainer>
    </StyledDialog>
  );
};

const ManageMonthlySpendWrapper = styled(Stack)`
  margin-top: ${theme.spaces.x32};
`;

const StyledInputContainer = styled(Stack)`
  margin-top: ${theme.spaces.x16};
`;

const StyledSeparator = styled(Separator)`
  margin: ${theme.spaces.x24} 0;
`;

const StyledUnorderedList = styled.ul`
  list-style-type: disc;
  padding-left: ${theme.spaces.x24};
`;

const StyledErrorContainer = styled(Stack)`
  margin-top: ${theme.spaces.x12};
  min-height: 20px;
`;

const StyledDialog = styled(Dialog)`
  max-height: 86vh;
`;
